import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useCopiar, useMediaQuery } from '@common/hooks';
import {
  Alert,
  Button,
  EmptyState,
  Input,
  Pagination,
  Select,
  SelectItem,
  Spinner,
  Tabs,
  useToast,
} from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { Download, SlidersHorizontal } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExcluirParticipantes, usePainel, useRestaurarParticipantes } from '../hooks';
import { useExportarParticipantes } from '../hooks/useExportacao';
import { AXIIS, useFiltrosParticipantes } from '../hooks/useFiltrosParticipantes';
import { POR_PAGINA_OPCOES, painelService } from '../services/painelService';
import type { AcaoLote, ItemPainel, SelecaoLote, SituacaoParticipante } from '../types/Painel';
import type { Fase } from '../types/Progresso';
import { AdicionarParticipanteModal } from './AdicionarParticipanteModal';
import { ColumnsPicker } from './ColumnsPicker';
import { ConfirmarLoteDialog } from './ConfirmarLoteDialog';
import { identificacao, ROTULO, SO_EXCLUIDOS, useColunasParticipantes } from './colunas';
import { EnviarConviteModal } from './EnviarConviteModal';
import { FiltrosDrawer } from './FiltrosDrawer';
import { selecaoDesatualizada } from './LembrarDialog';
import { type Ignorado, MotivosIgnoradosModal } from './MotivosIgnoradosModal';
import { ParticipanteDetalhe } from './ParticipanteDetalhe';
import { ParticipantesBarra } from './ParticipantesBarra';
import { ParticipantesChips } from './ParticipantesChips';
import { COMPACTA, ParticipantesTabela, type Selecao } from './ParticipantesTabela';
import { SelecionadosModal } from './SelecionadosModal';
import { TextoParaCopiar } from './TextoParaCopiar';

const DEBOUNCE_MS = 300;
const TODOS = 'todos';
/** Axiis com falha ou pendente: os dois valores do filtro (spec 24 B). */
const AMBOS = 'ambos';
const SEM_SELECAO: Selecao = { itens: new Map(), todosDoFiltro: false };
/** Cards de totais e o filtro de cada um (spec 29 R9); "Excluídos" saiu, a aba já o mostra (R11). */
const CARDS = [
  ['elegiveis', null],
  ['responderam', ['respondeu', 'sim']],
  ['naoResponderam', ['respondeu', 'nao']],
  ['queremBrinde', ['brinde', 'sim']],
  ['exterior', ['entrega', 'exterior']],
] as const;

/** Alvo da confirmação: a seleção da barra (`unico` ausente) ou uma pessoa (linha ou Drawer). */
type Confirmando = {
  acao: AcaoLote;
  unico?: { id: string; nome: string; matricula: number | null; respondeu: boolean };
};

export function ParticipantesAba({
  campanhaId,
  fase,
  comTotais = true,
}: {
  campanhaId: string;
  fase: Fase;
  /** No painel o funil já filtra a lista: sem os cartões de totais. */
  comTotais?: boolean;
}) {
  const { t } = useTranslation(['participantes', 'common', 'erros', 'convites', 'exportacao']);
  const { toast } = useToast();
  const compacta = useMediaQuery(COMPACTA);
  const colunas = useColunasParticipantes();
  const { filtros, alterar, limparFiltros, chaveDaSelecao, filtrosAtivos } =
    useFiltrosParticipantes();
  // `push`: o botão voltar do navegador fecha o Drawer.
  const [participante, setParticipante] = useQueryState(
    'participante',
    parseAsString.withOptions({ history: 'push' }),
  );

  // Busca com debounce: o campo é local; a URL (e a consulta) só mudam após a pausa na digitação.
  const [busca, setBusca] = useState(filtros.busca);
  useEffect(() => setBusca(filtros.busca), [filtros.busca]);
  useEffect(() => {
    if (busca === filtros.busca) return;
    const h = setTimeout(() => alterar({ busca }), DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [busca, filtros.busca, alterar]);

  const { data, isLoading, isError, error } = usePainel(campanhaId, filtros);
  const excluir = useExcluirParticipantes(campanhaId);
  const restaurar = useRestaurarParticipantes(campanhaId);

  const [selecao, setSelecao] = useState<Selecao>(SEM_SELECAO);
  const [confirmando, setConfirmando] = useState<Confirmando | null>(null);
  const [incluindo, setIncluindo] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [motivos, setMotivos] = useState<Ignorado[] | null>(null);
  const [desatualizada, setDesatualizada] = useState(false);
  const exportar = useExportarParticipantes(campanhaId);
  // 422 EXPORTACAO_MUITO_GRANDE: quantas pessoas a lista tem (spec 15 R6).
  const [excessoExportacao, setExcessoExportacao] = useState<number | null>(null);

  const [verSelecionados, setVerSelecionados] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const copia = useCopiar();
  const [copiados, setCopiados] = useState(0);

  // Spec 25 R3/R4: a seleção por ids sobrevive a busca e filtros; "todos do filtro" vale só para os
  // filtros em que foi feito, e trocar de aba (ids da outra situação) descarta tudo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a chave É a dependência
  useEffect(() => {
    setSelecao((s) =>
      s.todosDoFiltro || [...s.itens.values()].some((p) => p.situacao !== filtros.situacao)
        ? SEM_SELECAO
        : s,
    );
    setDesatualizada(false);
  }, [chaveDaSelecao]);

  const excluidos = filtros.situacao === 'excluido';
  const totais = data?.totais;
  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / filtros.porPagina));
  const marcada = (id: string) => selecao.todosDoFiltro || selecao.itens.has(id);
  const selecionados = selecao.todosDoFiltro ? total : selecao.itens.size;
  const paginaInteiraMarcada = itens.length > 0 && itens.every((p) => marcada(p.id));
  const foraDaLista = selecao.todosDoFiltro
    ? 0
    : [...selecao.itens.keys()].filter((id) => !itens.some((p) => p.id === id)).length;
  const nomesSelecionados = selecao.todosDoFiltro
    ? []
    : [...selecao.itens.values()].map(identificacao);
  // Incluir segue a sincronização: enquanto o prazo não terminou (spec 28 R5, R17).
  const podeIncluir = fase !== 'vencida' && fase !== 'encerrada' && fase !== 'fechada';
  // Convites só em agendada e aberta (P29).
  const podeEnviar = fase === 'agendada' || fase === 'aberta';
  const ativa = podeEnviar || fase === 'encerrada';

  function alternar(id: string) {
    setSelecao((s) => {
      const itensNovos = new Map(s.itens);
      if (itensNovos.has(id)) itensNovos.delete(id);
      else {
        const p = itens.find((i) => i.id === id);
        if (p) itensNovos.set(id, p);
      }
      return { itens: itensNovos, todosDoFiltro: false };
    });
  }

  function tirar(id: string) {
    setSelecao((s) => {
      const itensNovos = new Map(s.itens);
      itensNovos.delete(id);
      return { itens: itensNovos, todosDoFiltro: false };
    });
  }

  function alternarPagina() {
    setSelecao((s) => {
      if (s.todosDoFiltro) return SEM_SELECAO;
      const itensNovos = new Map(s.itens);
      const todas = itens.every((p) => itensNovos.has(p.id));
      for (const p of itens) {
        if (todas) itensNovos.delete(p.id);
        else itensNovos.set(p.id, p);
      }
      return { itens: itensNovos, todosDoFiltro: false };
    });
  }

  /** Quantas da seleção já responderam (para o aviso); `null` se não dá para saber. */
  function comResposta(): number | null {
    if (confirmando?.unico) return confirmando.unico.respondeu ? 1 : 0;
    if (!selecao.todosDoFiltro)
      return [...selecao.itens.values()].filter((p) => p.respondeu).length;
    // "Todos do filtro": só os totais (que ignoram filtros) ou um filtro de resposta dão a conta.
    if (filtros.respondeu.length === 1 && filtros.respondeu[0] === 'nao') return 0;
    if (filtros.respondeu.length === 1 && filtros.respondeu[0] === 'sim') return total;
    return filtrosAtivos ? null : (totais?.responderam ?? null);
  }

  /** A seleção da barra no formato da API (spec 13 R8). */
  function selecaoDaBarra(): SelecaoLote {
    const { pagina: _p, porPagina: _pp, ordenar: _o, direcao: _d, ...filtro } = filtros;
    return selecao.todosDoFiltro
      ? { filtro, totalEsperado: total }
      : { ids: [...selecao.itens.keys()] };
  }

  async function confirmar() {
    if (!confirmando) return;
    const { acao, unico } = confirmando;
    const selecaoApi: SelecaoLote = unico ? { ids: [unico.id] } : selecaoDaBarra();
    const nomes = new Map([...selecao.itens, ...itens.map((p) => [p.id, p] as const)]);
    try {
      const r = await (acao === 'excluir' ? excluir : restaurar).mutateAsync({
        selecao: selecaoApi,
      });
      setConfirmando(null);
      setSelecao(SEM_SELECAO);
      if (unico) setParticipante(null);
      const ignorados = r.ignorados.map((i) => ({
        nome: (i.id && nomes.get(i.id)?.nomeExibicao) || unico?.nome || '—',
        codigo: i.codigo,
      }));
      mostrarResultado(r.processados, ignorados);
    } catch (e) {
      setConfirmando(null);
      if (isAxiosError(e) && e.response?.data?.codigo === 'SELECAO_DESATUALIZADA') {
        setDesatualizada(true);
        setSelecao(SEM_SELECAO);
      } else toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
    }
  }

  /** E-mails da seleção da barra, em qualquer fase (spec 31 R14); 409 como no lote (R8). */
  async function copiarEmails() {
    const texto = painelService.emailsDaSelecao(campanhaId, selecaoDaBarra()).then((r) => {
      setCopiados(r.emails.length);
      return r.emails.join('; ');
    });
    try {
      await copia.copiar('emails', texto);
    } catch (e) {
      if (selecaoDesatualizada(e)) {
        setDesatualizada(true);
        setSelecao(SEM_SELECAO);
      } else toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
    }
  }

  async function exportarLista() {
    setExcessoExportacao(null);
    try {
      await exportar.mutateAsync(filtros);
    } catch (e) {
      if (isAxiosError(e) && e.response?.data?.codigo === 'EXPORTACAO_MUITO_GRANDE')
        setExcessoExportacao(Number(e.response.data.quantidade) || 0);
      else toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
    }
  }

  function mostrarResultado(processados: number, ignorados: Ignorado[]) {
    const opts = {
      title: ignorados.length
        ? t('lote.resultado', {
            processados: t('lote.processados', { count: processados }),
            ignorados: t('lote.ignoradosLote', { count: ignorados.length }),
          })
        : t('lote.soProcessados', { processados: t('lote.processados', { count: processados }) }),
      action: ignorados.length ? (
        <Button size="sm" variant="secondary" onClick={() => setMotivos(ignorados)}>
          {t('lote.verMotivos')}
        </Button>
      ) : undefined,
    };
    if (processados > 0) toast.success(opts);
    else toast.warning(opts);
  }

  const aoDevolverOuExcluir = (p: ItemPainel) =>
    setConfirmando({
      acao: p.situacao === 'elegivel' ? 'excluir' : 'restaurar',
      unico: { id: p.id, nome: p.nomeExibicao, matricula: p.matricula, respondeu: p.respondeu },
    });

  const rotuloColuna = (c: keyof typeof ROTULO) => t(`participantes:${ROTULO[c]}` as never);

  return (
    <section className="flex flex-col gap-4">
      <h2 className="sr-only">{t('participantes:aba')}</h2>
      {comTotais && totais && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {CARDS.map(([k, filtro]) => {
            // Pressionado: o filtro do campo é exatamente o do card; "Na campanha", sem filtro
            // nenhum (spec 29 R10).
            const pressionado = filtro
              ? filtros[filtro[0]].length === 1 && filtros[filtro[0]][0] === filtro[1]
              : !filtrosAtivos;
            return (
              <Button
                key={k}
                variant="secondary"
                aria-pressed={pressionado}
                className={`h-auto justify-start gap-2 px-4 py-2 ${pressionado ? 'border-brand-primary' : ''}`}
                onClick={() => {
                  // R9: "Na campanha" limpa tudo; os outros trocam só o próprio campo.
                  if (!filtro) limparFiltros();
                  else alterar({ [filtro[0]]: pressionado ? [] : [filtro[1]] });
                }}
              >
                {/* Número e rótulo na mesma linha, como o funil do painel. */}
                <span className="text-xl font-semibold text-foreground">{totais[k]}</span>
                <span className="text-sm text-foreground-muted">
                  {t(`participantes:painel.totais.${k}`)}
                </span>
              </Button>
            );
          })}
        </div>
      )}

      <Tabs
        value={filtros.situacao}
        onValueChange={(v) => alterar({ situacao: v as SituacaoParticipante })}
      >
        <Tabs.List>
          <Tabs.Trigger value="elegivel">
            {t('participantes:abas.naCampanha')} ({totais?.elegiveis ?? '…'})
          </Tabs.Trigger>
          <Tabs.Trigger value="excluido">
            {t('participantes:abas.excluidos')} ({totais?.excluidos ?? '…'})
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value={filtros.situacao} className="flex flex-col gap-3 pt-3">
          {/* Busca, filtro e ações numa linha a partir de `lg`: a tabela sobe. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
            <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0 [&_button]:min-w-0 md:flex md:flex-wrap md:items-center">
              <Input
                type="search"
                size="sm"
                className="col-span-2 w-full md:w-72"
                value={busca}
                aria-label={t('participantes:painel.filtros.busca')}
                placeholder={t('participantes:painel.filtros.busca')}
                onChange={(e) => setBusca(e.target.value)}
              />
              {!compacta && (
                <Select
                  size="sm"
                  // O `sm` do Select do Aluma 2.3.0 mantém 14 px; os vizinhos em `sm` têm 12 px.
                  className="[&_span]:text-xs"
                  value={filtros.axiis.length > 1 ? AMBOS : (filtros.axiis[0] ?? TODOS)}
                  aria-label={t('participantes:painel.filtros.axiis')}
                  onValueChange={(v) =>
                    alterar({
                      axiis:
                        v === TODOS ? [] : v === AMBOS ? [...AXIIS] : [v as 'falha' | 'pendente'],
                    })
                  }
                >
                  <SelectItem value={TODOS}>
                    {t('participantes:painel.filtros.rotulo', {
                      filtro: t('participantes:painel.filtros.axiis'),
                      valor: t('participantes:painel.filtros.todos'),
                    })}
                  </SelectItem>
                  {AXIIS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {t('participantes:painel.filtros.rotulo', {
                        filtro: t('participantes:painel.filtros.axiis'),
                        valor: t(`participantes:painel.filtros.${o}`),
                      })}
                    </SelectItem>
                  ))}
                  <SelectItem value={AMBOS}>
                    {t('participantes:painel.filtros.rotulo', {
                      filtro: t('participantes:painel.filtros.axiis'),
                      valor: t('participantes:painel.filtros.falhaOuPendente'),
                    })}
                  </SelectItem>
                </Select>
              )}
              {compacta ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<SlidersHorizontal size={14} />}
                  onClick={() => setFiltrosAbertos(true)}
                >
                  {t('participantes:filtros.avancados')}
                </Button>
              ) : (
                <ColumnsPicker
                  columnsState={colunas}
                  label={rotuloColuna}
                  available={(c) => excluidos || !SO_EXCLUIDOS.includes(c)}
                />
              )}
            </div>

            <ParticipantesBarra
              situacao={filtros.situacao}
              selecionados={selecionados}
              total={total}
              porPagina={filtros.porPagina}
              naPagina={itens.length}
              paginaInteiraMarcada={paginaInteiraMarcada}
              todosDoFiltro={selecao.todosDoFiltro}
              podeIncluir={podeIncluir}
              foraDaLista={foraDaLista}
              extras={
                // Só com linhas; `data` evita sumir e voltar enquanto o filtro carrega (spec 32 R8).
                data &&
                data.total >= 1 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download size={14} />}
                    loading={exportar.isPending}
                    onClick={exportarLista}
                  >
                    {t('exportacao:participantes.botao')}
                  </Button>
                )
              }
              onSelecionarTodos={() => setSelecao({ itens: new Map(), todosDoFiltro: true })}
              onVerSelecionados={() => setVerSelecionados(true)}
              onLimpar={() => setSelecao(SEM_SELECAO)}
              onAcaoLote={() => setConfirmando({ acao: excluidos ? 'restaurar' : 'excluir' })}
              onEnviarEmail={podeEnviar ? () => setEnviandoEmail(true) : undefined}
              onCopiarEmails={copiarEmails}
              copiados={copia.copiado === 'emails' ? copiados : null}
              textoManual={
                copia.manual && (
                  <TextoParaCopiar
                    texto={copia.manual.texto}
                    rotulo={t('convites:lembrar.copiarEmails')}
                  />
                )
              }
              onAdicionar={() => setIncluindo(true)}
            />
          </div>

          <ParticipantesChips filtros={filtros} onRemover={alterar} onLimpar={limparFiltros} />

          {desatualizada && (
            <Alert variant="warning" role="alert" onDismiss={() => setDesatualizada(false)}>
              {t('participantes:lote.desatualizada')}
            </Alert>
          )}

          {excessoExportacao !== null && (
            <Alert variant="danger" role="alert" onDismiss={() => setExcessoExportacao(null)}>
              {t('erros:EXPORTACAO_MUITO_GRANDE', { quantidade: excessoExportacao })}
            </Alert>
          )}

          {isLoading && <Spinner label={t('common:loading')} />}
          {isError && <Alert variant="danger" title={t(`erros:${mensagemDeErro(error)}`)} />}
          {data?.itens.length === 0 && (
            <EmptyState
              title={t(
                filtrosAtivos
                  ? 'participantes:painel.semResultado'
                  : excluidos
                    ? 'participantes:vazio.excluidos'
                    : 'participantes:painel.vazio',
              )}
            />
          )}

          {data && itens.length > 0 && (
            <>
              <ParticipantesTabela
                itens={itens}
                filtros={filtros}
                alterar={alterar}
                colunas={colunas.visibleColumns}
                selecao={selecao}
                onAlternar={alternar}
                onAlternarPagina={alternarPagina}
                onDetalhes={setParticipante}
                onAcao={aoDevolverOuExcluir}
              />
              <Pagination
                page={Math.min(filtros.pagina, totalPaginas)}
                totalPages={totalPaginas}
                onPageChange={(p) => alterar({ pagina: p })}
                pageSize={filtros.porPagina}
                pageSizeOptions={[...POR_PAGINA_OPCOES]}
                onPageSizeChange={(porPagina) => alterar({ porPagina })}
                totalItems={total}
                labels={{ pageSize: t('participantes:porPagina') }}
              />
            </>
          )}
        </Tabs.Content>
      </Tabs>

      <FiltrosDrawer
        open={filtrosAbertos}
        onClose={() => setFiltrosAbertos(false)}
        filtros={filtros}
        alterar={alterar}
        onLimpar={limparFiltros}
      />

      <ConfirmarLoteDialog
        acao={confirmando?.acao ?? null}
        quantidade={confirmando?.unico ? 1 : selecionados}
        comResposta={confirmando ? comResposta() : null}
        nomes={
          confirmando?.unico
            ? [
                identificacao({
                  nomeExibicao: confirmando.unico.nome,
                  matricula: confirmando.unico.matricula,
                }),
              ]
            : nomesSelecionados
        }
        pending={excluir.isPending || restaurar.isPending}
        onConfirmar={confirmar}
        onCancelar={() => setConfirmando(null)}
      />

      <AdicionarParticipanteModal
        campanhaId={campanhaId}
        open={incluindo}
        onClose={() => setIncluindo(false)}
        onResultado={mostrarResultado}
      />

      <EnviarConviteModal
        campanhaId={campanhaId}
        open={enviandoEmail}
        publico={selecionados}
        selecao={selecaoDaBarra()}
        itens={selecao.todosDoFiltro ? [] : [...selecao.itens.values()]}
        // Os filtros atuais com "sem e-mail"; a seleção por ids continua (spec 31 R19).
        onVerSemEmail={() => alterar({ temEmail: ['nao'] })}
        // Fica na lista e a barra limpa a seleção (R17).
        onEnviado={() => setSelecao(SEM_SELECAO)}
        onFechar={() => setEnviandoEmail(false)}
      />

      <SelecionadosModal
        itens={[...selecao.itens.values()]}
        open={verSelecionados && selecao.itens.size > 0}
        onClose={() => setVerSelecionados(false)}
        onTirar={tirar}
      />

      <MotivosIgnoradosModal ignorados={motivos} onClose={() => setMotivos(null)} />

      <ParticipanteDetalhe
        campanhaId={campanhaId}
        campanhaAtiva={ativa}
        podeCopiarLink={podeEnviar}
        participanteId={participante}
        onClose={() => setParticipante(null)}
        onAcao={(acao, d) =>
          setConfirmando({
            acao,
            unico: {
              id: d.id,
              nome: d.nomes.exibicao,
              matricula: d.matricula,
              respondeu: !!d.resposta,
            },
          })
        }
      />
    </section>
  );
}
